import { useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Layout } from "../../components/Layout";
import { usePermissions } from "../../providers/authProvider";
import { List } from "../../components/List";
import { OrderItem } from "../../components/order/OrderItem";
import { OrdersRequest, useOrders } from "../../hooks/useOrders";
import { Loader } from "../../components/Loader";
import { ORDER_STATUSES } from "../../../../types";
import { Button } from "../../components/Button";
import { ORDER_URL, USER_AUTH_URL } from "../../config";

const OrderList = ({
  label,
  ordersRequest,
  onRequestOrders,
  requestOrdersText,
  loadingText,
  noItemsText,
  admin,
}: {
  label: string;
  ordersRequest: OrdersRequest;
  onRequestOrders?: () => void;
  requestOrdersText?: string;
  loadingText: string;
  noItemsText: string;
  admin: boolean;
}) => {
  return (
    <>
      <h2>{label}</h2>
      {ordersRequest.status === "idle" ? (
        <div>
          {onRequestOrders && requestOrdersText ? (
            <>
              <Button
                onClick={onRequestOrders}
                btnType={"small"}
                btnColor={"nav"}
              >
                {requestOrdersText}
              </Button>
            </>
          ) : (
            "Something went wrong"
          )}
        </div>
      ) : ordersRequest.status === "loading" ? (
        <div>
          <div>
            <Loader type="text" showDots>
              {loadingText}
            </Loader>
          </div>
        </div>
      ) : ordersRequest.status === "present" ? (
        <List
          style="row"
          items={
            ordersRequest.orders?.map((order) => ({
              ...order,
              __meta: {
                key: order.slug,
                href: ORDER_URL(order.slug, admin),
                isAdmin: admin,
              },
            })) ?? []
          }
          Item={OrderItem}
          noItems={<>{noItemsText}</>}
        />
      ) : (
        <div>
          {/* {orderSlug} */}
          {ordersRequest.status}
          {typeof ordersRequest.error === "string"
            ? ordersRequest.error
            : JSON.stringify(ordersRequest.error)}
        </div>
      )}
    </>
  );
};

export default function Orders({ admin = false }: { admin?: boolean }) {
  const permissions = usePermissions();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (admin) {
      if (!permissions?.data?.loggedIn) {
        navigate(
          USER_AUTH_URL("login", { flow: "resume", to: location.pathname })
        );
        return;
      }
      if (!permissions?.data?.user?.admin) {
        navigate("/");
        return;
      }
    }
  }, [permissions, admin, navigate, location.pathname]);
  const [actionRequiredOrders] = useOrders({
    permissions,
    orderStatuses: [ORDER_STATUSES.PLACED.key, ORDER_STATUSES.IN_PROGRESS.key],
  });

  const [completedOrders, requestCompletedOrders] = useOrders({
    startWithRequest: false,
    permissions,
    orderStatuses: [ORDER_STATUSES.COMPLETE.key],
  });

  // // Set page title
  // useEffect(() => {
  //   document.title = "Orders | Doridium.com";
  // }, []);

  return (
    <>
      {/* TODO: Add meta tags with React Helmet or Vite SSG */}
      <Layout>
        <h2 className="text-textMain text-center p-[10px] mb-[10px] text-4xl">
          The {admin ? "admin" : ""} order list{" "}
        </h2>

        <div className="m-2">
          <OrderList
            admin={admin}
            label={"Action Required"}
            ordersRequest={actionRequiredOrders}
            loadingText="Checking to see if you have any orders to ship..."
            noItemsText="Your all done, go make some art!"
          />
        </div>
        <div className="m-2">
          <OrderList
            admin={admin}
            label={"Complete Orders"}
            ordersRequest={completedOrders}
            onRequestOrders={requestCompletedOrders}
            requestOrdersText={"Request completed order list"}
            loadingText="Loading your completed orders"
            noItemsText="No items completed, better start selling some art!"
          />
        </div>
      </Layout>
    </>
  );
}
